import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PermissionRolePage from "./PermissionRolePage";

// Import các API services của bạn
import userGroupApi from "../api/userGroupApi";
const pageNo = "RolePage";

function RolePage() {
  const user = useSelector((state) => state.auth.user);
  const customerInfor = useSelector((state) => state.auth.customerInfor);

  // --- Trạng thái tìm kiếm và lọc ---
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchParams, setSearchParams] = useState({
    searchKeyword: "",
  });
  const [reloadDataTrigger, setReloadDataTrigger] = useState(0);

  // --- Trạng thái DataGrid và phân trang ---
  const [userGroups, setUserGroups] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // --- Trạng thái Modal thêm, sửa vai trò ---
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [currentUserGroupToEdit, setCurrentUserGroupToEdit] = useState(null);

  // Refs for form fields
  const ugNameRef = useRef(null);
  const ugDescriptionRef = useRef(null);
  const itQuantityRef = useRef(null);
  const [ugIsActive, setUgIsActive] = useState(true); // Default active
  const [formErrors, setFormErrors] = useState({});

  // --- Trạng thái Modal xác nhận xóa ---
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [templateIdToDelete, setUserGroupIdToDelete] = useState(null);

  const [openEditPermissionModal, setOpenEditPermissionModal] = useState(false);
  const [editUserGroupId, setEditUserGroupId] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");

  // --- Hàm tìm kiếm vai trò ---
  const fetchUserGroup = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userGroupApi.searchUserGroups(
        customerInfor.CusId,
        searchParams.searchKeyword,
        paginationModel.page,
        paginationModel.pageSize,
        null,
      );
      if (response.success) {
        setUserGroups(response.data);
        setTotalRowCount(response.pagination?.totalRecords || 0);
      } else {
        setUserGroups([]);
        setTotalRowCount(0);
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi tìm kiếm vai trò.");
      setUserGroups([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchParams, paginationModel.page, paginationModel.pageSize]);

  // --- Phân trang DataGrid ---
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = () => {
    setSearchParams((prev) => ({
      searchKeyword: searchKeyword,
    }));
    // Reset trang về 0 khi thực hiện tìm kiếm mới
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setReloadDataTrigger((prev) => prev + 1);
  };

  // --- Thêm vai trò mới ---
  const handleAddUserGroup = () => {
    setCurrentUserGroupToEdit(null);
    if (ugNameRef.current) ugNameRef.current.value = "";
    if (ugDescriptionRef.current) ugDescriptionRef.current.value = "";
    setUgIsActive(true);
    setFormErrors({});
    setOpenAddEditModal(true);
  };

  // --- Sửa vai trò ---
  const handleEditUserGroup = (itId) => {
    const groupToEdit = userGroups.find((t) => t.UgId === itId);
    if (groupToEdit) {
      setCurrentUserGroupToEdit(groupToEdit);
      setUgIsActive(groupToEdit.UgIsActive);
      setFormErrors({});
      setOpenAddEditModal(true);
    } else {
      toast.error("Không tìm thấy vai trò để chỉnh sửa.");
    }
  };

  // --- Đóng Modal thêm/sửa ---
  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setCurrentUserGroupToEdit(null);
    setFormErrors({});
    if (ugNameRef.current) ugNameRef.current.value = "";
    if (ugDescriptionRef.current) ugDescriptionRef.current.value = "";
    setUgIsActive(true);
  };

  // --- Lưu vai trò (thêm mới hoặc cập nhật) ---
  const handleSaveUserGroup = async () => {
    const currentUgName = ugNameRef.current.value;
    const currentUgDescription = ugDescriptionRef.current.value;

    const errors = {};

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setLoading(true);
    try {
      const userGroupId = currentUserGroupToEdit
        ? currentUserGroupToEdit.UgId
        : null;
      const response = await userGroupApi.upsertUserGroup(
        userGroupId,
        currentUgName,
        currentUgDescription,
        ugIsActive,
        customerInfor.CusId,
      );

      if (response.success) {
        toast.success(response.message || "Lưu vai trò thành công!");
        handleCloseAddEditModal();
        handleSearch(); // Reload data
      } else {
        toast.error(response.message || "Lưu vai trò thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi lưu vai trò.");
    } finally {
      setLoading(false);
    }
  };

  // --- Mở Modal xác nhận xóa ---
  const handleOpenConfirmDeleteModal = (UgId) => {
    setUserGroupIdToDelete(UgId);
    setOpenConfirmDeleteModal(true);
  };

  // --- Đóng Modal xác nhận xóa ---
  const handleCloseConfirmDeleteModal = () => {
    setUserGroupIdToDelete(null);
    setOpenConfirmDeleteModal(false);
  };

  // --- Xóa vai trò ---
  const handleDeleteUserGroup = async () => {
    setLoading(true);
    try {
      const response = await userGroupApi.deleteUserGroup(templateIdToDelete); // <-- Sử dụng API thật
      if (response.success) {
        toast.success(response.message || "Xóa vai trò thành công!");
        handleCloseConfirmDeleteModal();
        handleSearch(); // Reload data
      } else {
        toast.error(response.message || "Xóa vai trò thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi xóa vai trò.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditRoleGroup = async (UgId, UgName) => {
    setEditUserGroupId(UgId);
    setOpenEditPermissionModal(true);
    setDialogTitle("Phân quyền cho nhóm: " + UgName);
  };
  const handleCloseEditRoleGroupModel = () => {
    setOpenEditPermissionModal(false);
  };
  // --- Định nghĩa các cột cho DataGrid ---
  const columns = [
    {
      field: "UgName",
      headerName: "Vai trò",
      headerAlign: "center",
      width: 300,
    },
    {
      field: "UgDescription",
      headerName: "Mô tả",
      headerAlign: "center",
      width: 500,
    },
    {
      field: "UgIsDefault",
      headerName: "Măc định",
      headerAlign: "center",
      type: "boolean",
      width: 100,
    },
    {
      field: "UgIsActive",
      headerName: "Kích hoạt",
      headerAlign: "center",
      type: "boolean",
      width: 100,
    },
    {
      field: "actions",
      headerName: "Chức năng",
      headerAlign: "center",
      type: "actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status =
          (customerInfor.CusIsAdmin && params.row.UgIsDefault) ||
          !params.row.UgIsDefault;
        return status ? (
          <Box>
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEditUserGroup(params.row.UgId)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleOpenConfirmDeleteModal(params.row.UgId)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                handleEditRoleGroup(params.row.UgId, params.row.UgName)
              }
              sx={{ ml: 1 }}
            >
              Phân quyền
            </Button>
          </Box>
        ) : (
          <></>
        );
      },
    },
  ];

  useEffect(() => {
    if (currentUserGroupToEdit) {
      if (ugNameRef.current)
        ugNameRef.current.value = currentUserGroupToEdit.UgName || "";
      if (ugDescriptionRef.current)
        ugDescriptionRef.current.value =
          currentUserGroupToEdit.UgDescription || "";
      if (itQuantityRef.current)
        itQuantityRef.current.value =
          currentUserGroupToEdit.ItQuantity !== undefined &&
          currentUserGroupToEdit.ItQuantity !== null
            ? currentUserGroupToEdit.ItQuantity
            : "";
    }
  }, [currentUserGroupToEdit]);

  useEffect(() => {
    fetchUserGroup();
  }, [fetchUserGroup, reloadDataTrigger]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Khung tìm kiếm */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Tên vai trò"
                fullWidth
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ height: "40px" }}
              >
                Tìm kiếm
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Nút thêm vai trò */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddUserGroup}
          >
            Thêm vai trò mới
          </Button>
        </Box>

        {/* DataGrid danh sách vai trò */}
        <Box sx={{ flexGrow: 1, width: "100%" }}>
          <DataGrid
            rows={userGroups}
            columns={columns}
            paginationMode="server"
            rowCount={totalRowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.UgId}
            showCellVerticalBorder
            showColumnVerticalBorder
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  Không có vai trò nào được tìm thấy.
                </Box>
              ),
            }}
          />
        </Box>

        {/* Modal Thêm/Sửa vai trò */}
        <Dialog open={openAddEditModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentUserGroupToEdit
              ? "Sửa thông tin vai trò"
              : "Thêm vai trò mới"}
          </DialogTitle>
          <DialogContent>
            <TextField
              maugin="dense"
              id="ugName"
              label="Tên vai trò"
              type="text"
              fullWidth
              variant="outlined"
              defaultValue={currentUserGroupToEdit?.UgName || ""}
              inputRef={ugNameRef}
              error={!!formErrors.ugName}
              helperText={formErrors.ugName}
              sx={{ mt: 1, mb: 1 }}
              size="small"
            />
            <TextField
              id="ugDescription"
              label="Mô tả vai trò"
              type="text"
              fullWidth
              variant="outlined"
              defaultValue={currentUserGroupToEdit?.UgDescription || ""}
              inputRef={ugDescriptionRef}
              error={!!formErrors.ugDescription}
              helperText={formErrors.ugDescription}
              sx={{ mb: 1 }}
              size="small"
              multiline
              rows={4}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={ugIsActive}
                  onChange={(event) => setUgIsActive(event.target.checked)}
                  name="ugIsActive"
                  color="primary"
                />
              }
              label="Hoạt động"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleSaveUserGroup}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Xác nhận xóa */}
        <Dialog open={openConfirmDeleteModal} maxWidth="xs" fullWidth>
          <DialogTitle>Xác nhận xóa</DialogTitle>
          <DialogContent>
            <Typography>
              Bạn có chắc chắn muốn xóa vai trò này không?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDeleteModal} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleDeleteUserGroup}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Xóa"}
            </Button>
          </DialogActions>
        </Dialog>

        <PermissionRolePage
          open={openEditPermissionModal}
          onClose={handleCloseEditRoleGroupModel}
          editUserGroupId={editUserGroupId}
          dialogTitle={dialogTitle}
        />
      </Box>
    </LocalizationProvider>
  );
}

export default RolePage;
